import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import { useToast } from '../components/Toast.js';
import { ActivityFeed } from '../components/ActivityFeed.js';
import { useOrg } from '../lib/org-context.js';
import { useAuth } from '../lib/auth-context.js';

// ===== TYPES =====

interface OrgSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
  subscriptionStatus: string | null;
  primaryDomain: string | null;
  createdAt: string;
  defaultBusiness: { id: string; legalName: string } | null;
  primaryLocation: { id: string; name: string; city: string | null; state: string | null } | null;
  leadCount: number;
}

// Display status: prioritize subscription status (what the customer sees) over lifecycle status (internal)
function displayStatus(org: { status: string; subscriptionStatus?: string | null }): string {
  if (org.subscriptionStatus === 'trial') return 'trial';
  if (org.subscriptionStatus === 'active') return 'active';
  if (org.subscriptionStatus === 'past_due') return 'past due';
  if (org.subscriptionStatus === 'cancelled') return 'cancelled';
  return org.status.replace(/_/g, ' ');
}

interface Business {
  id: string; type: string; country: string; legalName: string; dbaName: string | null;
  ein: string | null; bn: string | null; gstHstNumber: string | null;
  stateProvince: string | null; businessStructure: string | null;
  addressLine1: string | null; city: string | null; state: string | null; postalCode: string | null;
  billingEmail: string | null; billingPhone: string | null; billingContactName: string | null;
  taxExempt: boolean; currency: string; isDefault: boolean;
}

interface LocationData {
  id: string; name: string; businessId: string | null;
  addressLine1: string | null; city: string | null; state: string | null; zip: string | null;
  phone: string | null; email: string | null;
}

interface Lead {
  id: string; name: string; email: string; phone: string | null;
  sourcePage: string; status: string; createdAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  trial: 'bg-yellow-100 text-yellow-700',
  active: 'bg-green-100 text-green-700',
  'past due': 'bg-red-100 text-red-700',
  prospect: 'bg-yellow-100 text-yellow-700',
  in_build: 'bg-blue-100 text-blue-700',
  live: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-600',
  suspended: 'bg-red-100 text-red-700',
};

const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming','District of Columbia'];
const CA_PROVINCES = ['Alberta','British Columbia','Manitoba','New Brunswick','Newfoundland and Labrador','Northwest Territories','Nova Scotia','Nunavut','Ontario','Prince Edward Island','Quebec','Saskatchewan','Yukon'];
const STRUCTURES = [
  { value: 'llc', label: 'LLC' }, { value: 'corporation', label: 'Corporation' },
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' }, { value: 'partnership', label: 'Partnership' },
  { value: 'non_profit', label: 'Non-Profit' }, { value: 'other', label: 'Other' },
];

// ===== MAIN: CUSTOMER LIST =====

export function AdminOrgsPage() {
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [filtered, setFiltered] = useState<OrgSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [impersonatingOrgId, setImpersonatingOrgId] = useState<string | null>(null);
  const { toast } = useToast();
  const { selectOrg, deselectOrg } = useOrg();
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();

  const fetchOrgs = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await apiFetch<{ organizations: OrgSummary[] }>('/orgs/summary');
      setOrgs(data.organizations);
      setFiltered(data.organizations);
    } catch {
      // Fallback to basic list if summary endpoint not available
      try {
        const data = await apiFetch<{ organizations: OrgSummary[] }>('/orgs');
        setOrgs(data.organizations);
        setFiltered(data.organizations);
      } catch { /* */ }
    }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  useEffect(() => {
    let result = orgs;
    if (statusFilter) {
      result = result.filter(o => displayStatus(o) === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(o =>
        o.name.toLowerCase().includes(q) ||
        o.slug.toLowerCase().includes(q) ||
        o.defaultBusiness?.legalName.toLowerCase().includes(q) ||
        o.primaryLocation?.city?.toLowerCase().includes(q)
      );
    }
    setFiltered(result);
  }, [search, statusFilter, orgs]);

  const handleImpersonate = async (orgId: string, orgName: string) => {
    setImpersonatingOrgId(orgId);
    try {
      // Fetch the org's users to find the admin
      const usersData = await apiFetch<{ users: Array<{ id: string; role: string }> }>(`/orgs/${orgId}/users`);
      const adminUser = usersData.users.find(u => u.role === 'admin') ?? usersData.users[0];
      if (!adminUser) {
        toast('error', 'No users found for this account');
        setImpersonatingOrgId(null);
        return;
      }
      // Start impersonation
      const result = await apiFetch<{ access_token: string }>('/admin/impersonate', {
        method: 'POST',
        body: JSON.stringify({ target_user_id: adminUser.id }),
      });
      startImpersonation(result.access_token);
      selectOrg(orgId, orgName);
      navigate('/app/leads');
    } catch {
      toast('error', 'Failed to impersonate');
      setImpersonatingOrgId(null);
    }
  };

  if (showCreate) {
    return <CreateAccountPage onDone={() => { setShowCreate(false); fetchOrgs(); }} onCancel={() => setShowCreate(false)} />;
  }

  if (selectedOrgId) {
    return <AccountDetailPage orgId={selectedOrgId} onBack={() => { setSelectedOrgId(null); deselectOrg(); fetchOrgs(); }} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Accounts</h2>
          <p className="text-sm text-gray-500">{orgs.length} total</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700">
          + New Account
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text" placeholder="Search accounts..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {/* Status filters */}
      <div className="flex gap-2 flex-wrap">
        {['', 'trial', 'active', 'live', 'cancelled'].map(s => {
          const count = s ? orgs.filter(o => displayStatus(o) === s).length : orgs.length;
          return (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                statusFilter === s ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500 hover:text-gray-700'
              }`}>
              {s || 'All'} ({count})
            </button>
          );
        })}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-2">{[1,2,3,4].map(i => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse">
            <div className="flex items-center gap-3"><div className="w-10 h-10 bg-gray-200 rounded-lg" /><div className="flex-1"><div className="h-4 bg-gray-200 rounded w-40 mb-2" /><div className="h-3 bg-gray-100 rounded w-60" /></div></div>
          </div>
        ))}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-sm">{search ? 'No accounts match your search.' : 'No accounts yet.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(org => (
            <div key={org.id} className="bg-white rounded-xl border border-gray-100 p-4 hover:border-primary-200 hover:shadow-sm transition-all">
              <div className="flex items-center gap-3">
                {/* Clickable area for detail view */}
                <button
                  onClick={() => { setSelectedOrgId(org.id); selectOrg(org.id, org.name); }}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left active:scale-[0.99]"
                >
                  {/* Avatar */}
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-700 text-xs font-bold">{org.name.slice(0, 2).toUpperCase()}</span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 text-sm truncate">{org.name}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_COLORS[displayStatus(org)] ?? 'bg-gray-100 text-gray-600'}`}>
                        {displayStatus(org)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                      {org.defaultBusiness && <span>{org.defaultBusiness.legalName}</span>}
                      {org.defaultBusiness && org.primaryLocation?.city && <span className="text-gray-300">|</span>}
                      {org.primaryLocation?.city && <span>{org.primaryLocation.city}, {org.primaryLocation.state}</span>}
                    </div>
                    <p className="text-xs text-gray-300 mt-0.5 font-mono">{org.id}</p>
                  </div>

                  {/* Lead count */}
                  {org.leadCount > 0 && (
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-900">{org.leadCount}</p>
                      <p className="text-xs text-gray-400">leads</p>
                    </div>
                  )}
                </button>

                {/* Impersonate button */}
                <button
                  onClick={(e) => { e.stopPropagation(); handleImpersonate(org.id, org.name); }}
                  disabled={impersonatingOrgId === org.id}
                  className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors disabled:opacity-50"
                  title={`View as ${org.name}`}
                >
                  {impersonatingOrgId === org.id ? (
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                      ...
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View as
                    </span>
                  )}
                </button>

                {/* Chevron for detail */}
                <button
                  onClick={() => { setSelectedOrgId(org.id); selectOrg(org.id, org.name); }}
                  className="flex-shrink-0"
                >
                  <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== CREATE CUSTOMER (Single Page Form) =====

function CreateAccountPage({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [country, setCountry] = useState('US');

  const [account, setAccount] = useState({ name: '', domain: '' });
  const [biz, setBiz] = useState({
    legal_name: '', dba_name: '', ein: '', bn: '', gst_hst_number: '',
    business_structure: '', state_province: '',
    address_line1: '', city: '', state: '', postal_code: '',
    billing_contact_name: '', billing_email: '', billing_phone: '',
  });
  const [loc, setLoc] = useState({ name: '', address_line1: '', city: '', state: '', zip: '', phone: '', email: '' });

  // Pre-fill: account name -> legal name + location name
  const prevName = useRef('');
  useEffect(() => {
    if (account.name && account.name !== prevName.current) {
      setBiz(b => b.legal_name === '' || b.legal_name === prevName.current ? { ...b, legal_name: account.name } : b);
      setLoc(l => l.name === '' || l.name === prevName.current ? { ...l, name: account.name } : l);
      prevName.current = account.name;
    }
  }, [account.name]);

  const handleSave = async () => {
    if (!account.name) return;
    setIsSaving(true);
    try {
      // Create account + default business + location
      const orgResult = await apiFetch<{ org: { id: string } }>('/orgs', {
        method: 'POST',
        body: JSON.stringify({
          name: account.name,
          primary_domain: account.domain || undefined,
          location: {
            name: loc.name || account.name,
            address_line1: loc.address_line1 || undefined,
            city: loc.city || undefined,
            state: loc.state || undefined,
            zip: loc.zip || undefined,
            phone: loc.phone || undefined,
            email: loc.email || undefined,
          },
        }),
      });

      // Update the auto-created business with full details if any were filled
      if (biz.legal_name || biz.ein || biz.billing_email) {
        const bizData = await apiFetch<{ businesses: Array<{ id: string }> }>(`/orgs/${orgResult.org.id}/businesses`);
        if (bizData.businesses[0]) {
          await apiFetch(`/orgs/${orgResult.org.id}/businesses/${bizData.businesses[0].id}`, {
            method: 'PUT',
            body: JSON.stringify({
              legal_name: biz.legal_name || account.name,
              dba_name: biz.dba_name || undefined,
              ein: biz.ein || undefined,
              bn: biz.bn || undefined,
              gst_hst_number: biz.gst_hst_number || undefined,
              business_structure: biz.business_structure || undefined,
              state_province: biz.state_province || undefined,
              address_line1: biz.address_line1 || undefined,
              city: biz.city || undefined,
              state: biz.state || undefined,
              postal_code: biz.postal_code || undefined,
              billing_contact_name: biz.billing_contact_name || undefined,
              billing_email: biz.billing_email || undefined,
              billing_phone: biz.billing_phone || undefined,
              country,
              currency: country === 'CA' ? 'CAD' : 'USD',
            }),
          });
        }
      }

      toast('success', `${account.name} created`);
      onDone();
    } catch {
      toast('error', 'Failed to create account');
    } finally {
      setIsSaving(false);
    }
  };

  const stateOptions = country === 'US' ? US_STATES : CA_PROVINCES;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={onCancel} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Cancel
          </button>
          <h2 className="text-lg font-semibold text-gray-900">New Account</h2>
        </div>
      </div>

      {/* Section 1: Account */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
            <span className="text-primary-700 text-xs font-bold">1</span>
          </div>
          <h3 className="text-sm font-medium text-gray-900">Account</h3>
        </div>
        <Field label="Account Name *" value={account.name} onChange={v => setAccount(a => ({ ...a, name: v }))} placeholder="Downtown Martial Arts" />
        <Field label="Website Domain" value={account.domain} onChange={v => setAccount(a => ({ ...a, domain: v }))} placeholder="www.downtownma.com" />
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Country</label>
          <select value={country} onChange={e => setCountry(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="US">United States</option>
            <option value="CA">Canada</option>
          </select>
        </div>
      </div>

      {/* Section 2: Business */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
            <span className="text-primary-700 text-xs font-bold">2</span>
          </div>
          <h3 className="text-sm font-medium text-gray-900">Business Details</h3>
          <span className="text-xs text-gray-400">(optional -- can complete later)</span>
        </div>
        <Field label="Legal Business Name" value={biz.legal_name} onChange={v => setBiz(b => ({ ...b, legal_name: v }))} placeholder={account.name || 'Auto-filled from account name'} />
        <Field label="DBA / Trade Name" value={biz.dba_name} onChange={v => setBiz(b => ({ ...b, dba_name: v }))} placeholder="Downtown MA" />
        <div className="grid grid-cols-2 gap-2">
          {country === 'US' ? (
            <EinField value={biz.ein} onChange={v => setBiz(b => ({ ...b, ein: v }))} />
          ) : (
            <>
              <Field label="Business Number (BN)" value={biz.bn} onChange={v => setBiz(b => ({ ...b, bn: v }))} placeholder="123456789" />
              <Field label="GST/HST" value={biz.gst_hst_number} onChange={v => setBiz(b => ({ ...b, gst_hst_number: v }))} placeholder="123456789RT0001" />
            </>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Structure</label>
            <select value={biz.business_structure} onChange={e => setBiz(b => ({ ...b, business_structure: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <option value="">Select...</option>
              {STRUCTURES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>
        <StateAutocomplete label={country === 'US' ? 'State of Incorporation' : 'Province of Incorporation'} value={biz.state_province} options={stateOptions} onChange={v => setBiz(b => ({ ...b, state_province: v }))} />
        <div className="grid grid-cols-3 gap-2">
          <Field label="Billing Contact" value={biz.billing_contact_name} onChange={v => setBiz(b => ({ ...b, billing_contact_name: v }))} />
          <Field label="Billing Email" value={biz.billing_email} onChange={v => setBiz(b => ({ ...b, billing_email: v }))} type="email" />
          <Field label="Billing Phone" value={biz.billing_phone} onChange={v => setBiz(b => ({ ...b, billing_phone: v }))} type="tel" />
        </div>
      </div>

      {/* Section 3: Location */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
            <span className="text-primary-700 text-xs font-bold">3</span>
          </div>
          <h3 className="text-sm font-medium text-gray-900">First Location</h3>
          <span className="text-xs text-gray-400">(optional -- can complete later)</span>
        </div>
        <Field label="Location Name" value={loc.name} onChange={v => setLoc(l => ({ ...l, name: v }))} placeholder={account.name || 'Auto-filled from account name'} />
        <Field label="Address" value={loc.address_line1} onChange={v => setLoc(l => ({ ...l, address_line1: v }))} placeholder="123 Main St" />
        <div className="grid grid-cols-3 gap-2">
          <Field label="City" value={loc.city} onChange={v => setLoc(l => ({ ...l, city: v }))} />
          <Field label={country === 'US' ? 'State' : 'Province'} value={loc.state} onChange={v => setLoc(l => ({ ...l, state: v }))} />
          <Field label={country === 'US' ? 'ZIP' : 'Postal Code'} value={loc.zip} onChange={v => setLoc(l => ({ ...l, zip: v }))} placeholder={country === 'US' ? '12345' : 'A1A 1A1'} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Phone" value={loc.phone} onChange={v => setLoc(l => ({ ...l, phone: v }))} type="tel" />
          <Field label="Email" value={loc.email} onChange={v => setLoc(l => ({ ...l, email: v }))} type="email" />
        </div>
      </div>

      {/* Save */}
      <div className="sticky bottom-16 md:bottom-0 bg-gray-50 py-3 -mx-4 px-4">
        <button onClick={handleSave} disabled={!account.name || isSaving}
          className="w-full bg-primary-600 text-white py-3 rounded-xl font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors">
          {isSaving ? 'Creating...' : 'Create Account'}
        </button>
      </div>
    </div>
  );
}

// ===== CUSTOMER DETAIL (Tabbed) =====

type DetailTab = 'overview' | 'business' | 'locations' | 'leads' | 'notifications' | 'campaigns';

function AccountDetailPage({ orgId, onBack }: { orgId: string; onBack: () => void }) {
  const [tab, setTab] = useState<DetailTab>('overview');
  const [org, setOrg] = useState<{ name: string; slug: string; status: string; primaryDomain: string | null; settings: Record<string, unknown> | null; createdAt: string } | null>(null);
  const [bizes, setBizes] = useState<Business[]>([]);
  const [locs, setLocs] = useState<LocationData[]>([]);
  const [recentLeads, setRecentLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingBizId, setEditingBizId] = useState<string | null>(null);
  const [showAddBiz, setShowAddBiz] = useState(false);
  const { toast } = useToast();

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [orgData, bizData] = await Promise.all([
        apiFetch<{ name: string; slug: string; status: string; primaryDomain: string | null; settings: Record<string, unknown> | null; createdAt: string; locations: LocationData[] }>(`/orgs/${orgId}`),
        apiFetch<{ businesses: Business[] }>(`/orgs/${orgId}/businesses`),
      ]);
      setOrg(orgData);
      setLocs(orgData.locations);
      setBizes(bizData.businesses);

      // Fetch recent leads (try, non-blocking)
      try {
        const leadData = await apiFetch<{ leads: Lead[] }>(`/leads?org_id=${orgId}&limit=10`);
        setRecentLeads(leadData.leads);
      } catch { /* leads may not be accessible */ }
    } catch { /* */ }
    finally { setIsLoading(false); }
  }, [orgId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (isLoading || !org) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}</div>;
  }

  const TABS: { key: DetailTab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'business', label: `Business${bizes.length > 1 ? `es (${bizes.length})` : ''}` },
    { key: 'locations', label: `Location${locs.length > 1 ? `s (${locs.length})` : ''}` },
    { key: 'leads', label: `Leads (${recentLeads.length})` },
    { key: 'notifications', label: 'Notifications' },
    { key: 'campaigns', label: 'Campaigns' },
  ];

  return (
    <div className="space-y-4">
      {/* Back + Account Header */}
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Accounts
      </button>

      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
            <span className="text-primary-700 font-bold">{org.name.slice(0, 2).toUpperCase()}</span>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{org.name}</h2>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className={`px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[displayStatus(org)] ?? 'bg-gray-100 text-gray-600'}`}>
                {displayStatus(org)}
              </span>
              <span>{org.slug}</span>
              {org.primaryDomain && <><span className="text-gray-300">|</span><span>{org.primaryDomain}</span></>}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex-1 py-2 px-3 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
              tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'overview' && (
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Summary</h3>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><p className="text-2xl font-bold text-primary-600">{bizes.length}</p><p className="text-xs text-gray-500">{bizes.length === 1 ? 'Business' : 'Businesses'}</p></div>
              <div><p className="text-2xl font-bold text-green-600">{locs.length}</p><p className="text-xs text-gray-500">{locs.length === 1 ? 'Location' : 'Locations'}</p></div>
              <div><p className="text-2xl font-bold text-gray-900">{recentLeads.length}</p><p className="text-xs text-gray-500">Leads</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Account Details</h3>
            <div className="space-y-2 text-sm">
              <Row label="Slug" value={org.slug} />
              <Row label="Domain" value={org.primaryDomain ?? 'Not set'} />
              <Row label="Status" value={
                <div className="flex items-center gap-2">
                  <select
                    value={org.status}
                    onChange={async (e) => {
                      const newStatus = e.target.value;
                      const reason = prompt(`Reason for changing status to "${newStatus}":`);
                      if (!reason) return;
                      try {
                        await apiFetch(`/admin/orgs/${orgId}/status`, { method: 'PUT', body: JSON.stringify({ status: newStatus, reason }) });
                        toast('success', `Status changed to ${newStatus}`);
                        fetchAll();
                      } catch { toast('error', 'Failed to change status'); }
                    }}
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {['prospect', 'in_build', 'approval_needed', 'live', 'pending_cancelled', 'cancelled'].map(s => (
                      <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
              } />
              <Row label="Created" value={new Date(org.createdAt).toLocaleDateString()} />
            </div>
          </div>
          {/* Website Provisioning */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Website</h3>
            {org.primaryDomain ? (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-sm text-green-700 font-medium">Live</span>
                <a href={`https://${org.primaryDomain}`} target="_blank" rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:text-primary-800 underline ml-auto">{org.primaryDomain}</a>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-500">No website deployed yet.</p>
                <button
                  onClick={async () => {
                    try {
                      await apiFetch(`/admin/orgs/${orgId}/provision-site`, { method: 'POST', body: JSON.stringify({ template: 'core1' }) });
                      toast('success', 'Site provisioning triggered');
                    } catch { toast('error', 'Failed to trigger provisioning'); }
                  }}
                  className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Provision Website
                </button>
              </div>
            )}
          </div>
          <ActivityFeed orgId={orgId} title="Recent Activity" limit={15} />
        </div>
      )}

      {tab === 'business' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button onClick={() => setShowAddBiz(!showAddBiz)} className="text-sm text-primary-600 hover:text-primary-800 font-medium">+ Add Business</button>
          </div>
          {showAddBiz && <BusinessEditForm businessCount={bizes.length} onSave={async (input) => {
            try { await apiFetch(`/orgs/${orgId}/businesses`, { method: 'POST', body: JSON.stringify(input) }); toast('success', 'Business created'); setShowAddBiz(false); fetchAll(); } catch { toast('error', 'Failed'); }
          }} onCancel={() => setShowAddBiz(false)} />}
          {bizes.map(biz => (
            <div key={biz.id}>
              {editingBizId === biz.id ? (
                <BusinessEditForm business={biz} businessCount={bizes.length} onSave={async (input) => {
                  try { await apiFetch(`/orgs/${orgId}/businesses/${biz.id}`, { method: 'PUT', body: JSON.stringify(input) }); toast('success', 'Updated'); setEditingBizId(null); fetchAll(); } catch { toast('error', 'Failed'); }
                }} onCancel={() => setEditingBizId(null)} />
              ) : (
                <div className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 text-sm">{biz.legalName}</p>
                        {biz.isDefault && <span className="text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded font-medium">Default</span>}
                        <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{biz.country}</span>
                      </div>
                      {biz.dbaName && <p className="text-xs text-gray-500 mt-0.5">DBA: {biz.dbaName}</p>}
                    </div>
                    <button onClick={() => setEditingBizId(biz.id)} className="text-xs text-primary-600 font-medium">Edit</button>
                  </div>
                  <div className="text-xs text-gray-400 space-y-0.5">
                    {biz.ein && <p>EIN: {biz.ein}</p>}
                    {biz.bn && <p>BN: {biz.bn}</p>}
                    {biz.businessStructure && <p className="capitalize">{biz.businessStructure.replace('_', ' ')}</p>}
                    {biz.city && <p>{biz.addressLine1}, {biz.city}, {biz.state} {biz.postalCode}</p>}
                    {biz.billingEmail && <p>Billing: {biz.billingEmail}</p>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'locations' && (
        <div className="space-y-2">
          {locs.map(loc => (
            <div key={loc.id} className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="font-medium text-gray-900 text-sm">{loc.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {[loc.addressLine1, loc.city, loc.state].filter(Boolean).join(', ') || 'No address set'}
              </p>
              {loc.phone && <p className="text-xs text-gray-400 mt-0.5">{loc.phone}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'leads' && (
        <div className="space-y-2">
          {recentLeads.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">No leads yet.</div>
          ) : recentLeads.map(lead => (
            <div key={lead.id} className="bg-white rounded-xl border border-gray-100 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{lead.name}</p>
                  <p className="text-xs text-gray-500">{lead.email}{lead.phone ? ` -- ${lead.phone}` : ''}</p>
                </div>
                <span className="text-xs text-gray-400">{new Date(lead.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'notifications' && <NotificationsTab orgId={orgId} />}
      {tab === 'campaigns' && <CampaignsTab orgId={orgId} />}
    </div>
  );
}

// ===== NOTIFICATIONS TAB =====

function NotificationsTab({ orgId }: { orgId: string }) {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchNotifs = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('limit', '50');
      const d = await apiFetch<{ notifications: Record<string, unknown>[] }>(`/admin/orgs/${orgId}/notifications?${params}`);
      setRows(d.notifications);
    } catch { /* */ }
    finally { setIsLoading(false); }
  }, [orgId, statusFilter]);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);

  const statusColors: Record<string, string> = {
    sent: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    failed: 'bg-red-100 text-red-700',
    skipped: 'bg-gray-100 text-gray-600',
    queued: 'bg-blue-100 text-blue-700',
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        {['', 'sent', 'failed', 'pending', 'skipped'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
              statusFilter === s ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}>{s || 'All'}</button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <p className="text-center py-8 text-sm text-gray-500">No notifications found.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((n) => (
            <div key={n.id as string} className="bg-white rounded-xl border border-gray-100 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${statusColors[n.status as string] ?? 'bg-gray-100 text-gray-600'}`}>
                      {n.status as string}
                    </span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-50 text-gray-600">{n.channel as string}</span>
                    <span className="text-sm text-gray-900">{(n.leadName as string) ?? 'Unknown'}</span>
                  </div>
                  {n.errorMessage && (
                    <p className="text-xs text-red-500 mt-1">{n.errorMessage as string}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">{(n.leadEmail as string) ?? ''}</p>
                </div>
                <span className="text-xs text-gray-400">{new Date(n.createdAt as string).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== CAMPAIGNS TAB =====

function CampaignsTab({ orgId }: { orgId: string }) {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [stuckCount, setStuckCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchExecs = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('limit', '50');
      const d = await apiFetch<{ executions: Record<string, unknown>[]; stuckCount: number }>(`/admin/orgs/${orgId}/drip-executions?${params}`);
      setRows(d.executions);
      setStuckCount(d.stuckCount);
    } catch { /* */ }
    finally { setIsLoading(false); }
  }, [orgId, statusFilter]);

  useEffect(() => { fetchExecs(); }, [fetchExecs]);

  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    completed: 'bg-blue-100 text-blue-700',
    paused: 'bg-yellow-100 text-yellow-700',
    cancelled: 'bg-gray-100 text-gray-600',
    processing: 'bg-purple-100 text-purple-700',
  };

  return (
    <div className="space-y-3">
      {stuckCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
          <div className="w-2 h-2 bg-red-500 rounded-full" />
          <p className="text-sm text-red-700 font-medium">{stuckCount} stuck execution{stuckCount > 1 ? 's' : ''} (processing &gt; 5 min)</p>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {['', 'active', 'completed', 'paused', 'cancelled'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
              statusFilter === s ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}>{s || 'All'}</button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <p className="text-center py-8 text-sm text-gray-500">No drip enrollments found.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((exec) => (
            <div key={exec.id as string} className="bg-white rounded-xl border border-gray-100 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${statusColors[exec.status as string] ?? 'bg-gray-100 text-gray-600'}`}>
                      {exec.status as string}
                    </span>
                    <span className="text-sm text-gray-900">{exec.leadName as string}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {exec.sequenceName as string} — Step {exec.currentStep as number}
                    {exec.nextStepDueAt && (exec.status as string) === 'active' && ` — Next: ${new Date(exec.nextStepDueAt as string).toLocaleDateString()}`}
                  </p>
                  <p className="text-xs text-gray-400">{exec.leadEmail as string} — Enrolled {new Date(exec.enrolledAt as string).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== BUSINESS EDIT FORM (Compact) =====

function BusinessEditForm({ business, businessCount = 0, onSave, onCancel }: {
  business?: Business; businessCount?: number;
  onSave: (data: Record<string, unknown>) => void; onCancel: () => void;
}) {
  const [country] = useState(business?.country ?? 'US');
  const isFirst = !business && businessCount === 0;
  const showDefault = business ? businessCount > 1 : businessCount > 0;

  const [form, setForm] = useState({
    legal_name: business?.legalName ?? '', dba_name: business?.dbaName ?? '',
    ein: business?.ein ?? '', bn: business?.bn ?? '', gst_hst_number: business?.gstHstNumber ?? '',
    business_structure: business?.businessStructure ?? '', state_province: business?.stateProvince ?? '',
    address_line1: business?.addressLine1 ?? '', city: business?.city ?? '', state: business?.state ?? '',
    postal_code: business?.postalCode ?? '',
    billing_contact_name: business?.billingContactName ?? '', billing_email: business?.billingEmail ?? '',
    billing_phone: business?.billingPhone ?? '', tax_exempt: business?.taxExempt ?? false,
    is_default: business?.isDefault ?? isFirst, country, currency: country === 'CA' ? 'CAD' : 'USD',
  });

  const stateOptions = country === 'US' ? US_STATES : CA_PROVINCES;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
      <h3 className="font-medium text-gray-900 text-sm">{business ? 'Edit Business' : 'Add Business'}</h3>
      <Field label="Legal Name *" value={form.legal_name} onChange={v => setForm(f => ({ ...f, legal_name: v }))} />
      <Field label="DBA" value={form.dba_name} onChange={v => setForm(f => ({ ...f, dba_name: v }))} />
      <div className="grid grid-cols-2 gap-2">
        {country === 'US' ? (
          <EinField value={form.ein} onChange={v => setForm(f => ({ ...f, ein: v }))} />
        ) : (
          <Field label="BN" value={form.bn} onChange={v => setForm(f => ({ ...f, bn: v }))} />
        )}
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Structure</label>
          <select value={form.business_structure} onChange={e => setForm(f => ({ ...f, business_structure: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">Select...</option>{STRUCTURES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>
      <StateAutocomplete label={country === 'US' ? 'State of Incorp.' : 'Province'} value={form.state_province} options={stateOptions} onChange={v => setForm(f => ({ ...f, state_province: v }))} />
      {showDefault && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_default} onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))} className="rounded" />
          <span className="text-gray-700">Make this the default business</span>
        </label>
      )}
      <div className="flex gap-2">
        <button onClick={() => onSave(form)} disabled={!form.legal_name} className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">{business ? 'Save' : 'Create'}</button>
        <button onClick={onCancel} className="text-sm text-gray-500 px-3 py-2">Cancel</button>
      </div>
    </div>
  );
}

// ===== SHARED COMPONENTS =====

function Field({ label, value, onChange, type = 'text', placeholder }: { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
      <span className="text-sm text-gray-900">{value}</span>
    </div>
  );
}

function EinField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const format = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 9);
    return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
  };
  const formatted = format(value);
  const complete = value.replace(/\D/g, '').length === 9;

  return (
    <div>
      <div className="flex items-center gap-1 mb-1">
        <label className="text-xs font-medium text-gray-600">EIN</label>
        <div className="group relative">
          <svg className="w-3.5 h-3.5 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50">
            <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 w-52 shadow-lg">
              <p className="font-medium">Employer Identification Number</p>
              <p>Format: XX-XXXXXXX (9 digits)</p>
            </div>
          </div>
        </div>
      </div>
      <input type="text" value={formatted} onChange={e => onChange(format(e.target.value))} placeholder="XX-XXXXXXX" maxLength={10}
        className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${complete ? 'border-green-300 bg-green-50' : 'border-gray-200'}`} />
    </div>
  );
}

function StateAutocomplete({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (v: string) => void }) {
  const [search, setSearch] = useState(value);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const filtered = search ? options.filter(s => s.toLowerCase().includes(search.toLowerCase())) : options;

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input type="text" value={search || value} onChange={e => { setSearch(e.target.value); setOpen(true); if (!e.target.value) onChange(''); }}
        onFocus={() => setOpen(true)} placeholder="Start typing..."
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {filtered.map(s => (
            <button key={s} onClick={() => { onChange(s); setSearch(s); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-primary-50">{s}</button>
          ))}
        </div>
      )}
    </div>
  );
}
